import { useEffect, useState } from "react";
import {
    Typography,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Box,
    Chip,
    Button,
    Stack,
    Modal,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
} from "@mui/material";
import Layout from "../../../components/layouts/Layout";
import { useNavigate } from "react-router-dom";
import SuperAdminCompanyService from "../../../service/superadmin/companyService";
import { toast } from "react-toastify";
import { Company } from "../../../types/companyTypes";
import { AdminSpace } from "../../../types/spaceTypes";
import ConfirmDialog from "../../../components/dialog/ConfirmationDialog";
import React from "react";

const statusColors: Record<string, "success" | "error"> = {
    active: "success",
    inactive: "error",
};

const CompanyList = () => {
    const [companies, setCompanies] = useState<Company[]>([]);
    const [adminSpaces, setAdminSpaces] = useState<AdminSpace[]>([]);

    const isCompanyBlocked = adminSpaces.length === 0;

    const [loading, setLoading] = useState<boolean>(true);

    const [openNew, setOpenNew] = useState<boolean>(false);
    const [openEdit, setOpenEdit] = useState<boolean>(false);
    const [openDelete, setOpenDelete] = useState<boolean>(false);

    const [newCompanyName, setNewCompanyName] = useState<string>('');
    const [selectedAdminSpace, setSelectedAdminSpace] = useState<string>('');
    const [removeCompanyId, setRemoveCompanyId] = useState<string>('');

    const [openRow, setOpenRow] = useState<number | null>(null);

    const [editFormData, setEditFormData] = useState({
        id: '',
        company_name: '',
        status: '',
    });

    const navigate = useNavigate();

    const handleToggleRow = (companyId: number) => {
        setOpenRow(openRow === companyId ? null : companyId);
    };

    const fetchCompanyList = async () => {
        try {
            const data = await SuperAdminCompanyService.getCompanyList();
            setCompanies(data);
        } catch (error) {
            toast.error("Failed to fetch companies");
        } finally {
            setLoading(false);
        }
    };

    const fetchAdminSpaceList = async () => {
        try {
            const data = await SuperAdminCompanyService.getAdminSpaceList();
            setAdminSpaces(data);
        } catch (error) {
            toast.error("Failed to fetch admin's spaces");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCompanyList();
    }, []);

    const handleOpenAddNew = async () => {
        await fetchAdminSpaceList();
        if (adminSpaces) {
            setOpenNew(true);
        }
    }

    const handleAddNew = async () => {
        try {
            await SuperAdminCompanyService.createCompany({
                company_name: newCompanyName,
                admin_space_id: selectedAdminSpace,
            });
            toast.success("Company created successfully!");

            setOpenNew(false);
            fetchCompanyList();

            setNewCompanyName('');
            setSelectedAdminSpace('');
        } catch (error) {
            toast.error("Failed to create company.");
        }
    }

    const handleOpenEdit = (company: Company) => {
        setEditFormData({
            id: company.id.toString(),
            company_name: company.company_name,
            status: company.status,
        });
        setOpenEdit(true);
    };

    const handleEdit = async () => {
        try {
            await SuperAdminCompanyService.updateCompanyName(
                editFormData.id,
                { company_name: editFormData.company_name, status: editFormData.status }
            );
            toast.success("Company updated successfully!");
            setOpenEdit(false);
            fetchCompanyList();
            setEditFormData({
                id: '',
                company_name: '',
                status: '',
            });
        } catch (error) {
            toast.error(`Failed to update company : ${error}`);
        }
    };

    const confirmDelete = (companyId: string) => {
        setRemoveCompanyId(companyId);
        setOpenDelete(true);
    };

    const handleDelete = async () => {
        try {
            await SuperAdminCompanyService.deleteCompany(removeCompanyId);
            toast.success("Company deleted successfully!");
            fetchCompanyList();
        } catch (error) {
            toast.error(`Failed to delete company : ${error}`);
        } finally {
            setOpenDelete(false);
            setRemoveCompanyId('');
        }
    };

    return (
        <Layout>
            {/* Title & Add New User Button */}
            <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="h5" fontWeight="bold">
                    Company List
                </Typography>
                <Button variant="contained" color="primary" sx={{ color: "white" }} onClick={handleOpenAddNew}>
                    Add New Company
                </Button>
            </Box>

            {loading ? (
                <CircularProgress />
            ) : companies.length === 0 ? (
                <div className="text-center text-gray-500 py-10">
                    <p>No company.</p>
                    <p className="mt-2">Create a new company to get started.</p>
                </div>
            ) : (
                <TableContainer component={Paper} sx={{ borderRadius: 2, overflow: "hidden", boxShadow: "none" }}>
                    <Table sx={{ background: "white" }}>
                        <TableHead>
                            <TableRow>
                                <TableCell>#</TableCell>
                                <TableCell>Company Name</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Action</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {companies.map((company, index) => {
                                const mappedStatus = company.status === "1" ? "active" : "inactive";
                                const isOpen = openRow === company.id;

                                return (
                                    <React.Fragment key={company.id}>
                                        <TableRow>
                                            <TableCell>{index + 1}</TableCell>
                                            <TableCell>{company.company_name}</TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={mappedStatus}
                                                    color={statusColors[mappedStatus]}
                                                    sx={{ textTransform: "capitalize", fontWeight: 500 }}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Stack direction="row" spacing={1}>
                                                    <Button
                                                        variant="contained"
                                                        sx={{ backgroundColor: "#17a2b8", color: "white" }}
                                                        onClick={() => handleToggleRow(company.id)}
                                                    >
                                                        {isOpen ? "Hide Users" : "View Users"}
                                                    </Button>
                                                    <Button
                                                        variant="contained"
                                                        sx={{ backgroundColor: "#6c757d", color: "white" }}
                                                        onClick={() => handleOpenEdit(company)}
                                                    >
                                                        Edit
                                                    </Button>
                                                    <Button
                                                        variant="contained"
                                                        sx={{ backgroundColor: "#dc3545", color: "white" }}
                                                        onClick={() => confirmDelete(company.id.toString())}
                                                    >
                                                        Delete
                                                    </Button>
                                                </Stack>
                                            </TableCell>
                                        </TableRow>

                                        {/* Expanded User List */}
                                        {isOpen && (
                                            <TableRow>
                                                <TableCell colSpan={4} sx={{ backgroundColor: "#f9f9f9" }}>
                                                    {company.users && company.users.length > 0 ? (
                                                        <Table size="small">
                                                            <TableHead>
                                                                <TableRow>
                                                                    <TableCell>#</TableCell>
                                                                    <TableCell>User Name</TableCell>
                                                                    <TableCell>Email</TableCell>
                                                                    <TableCell>Phone</TableCell>
                                                                    <TableCell>Status</TableCell>
                                                                </TableRow>
                                                            </TableHead>
                                                            <TableBody>
                                                                {company.users.map((user, index) => (
                                                                    <TableRow key={user.id}>
                                                                        <TableCell>{index + 1}</TableCell>
                                                                        <TableCell>{user.name}</TableCell>
                                                                        <TableCell>{user.email}</TableCell>
                                                                        <TableCell>{user.phone}</TableCell>
                                                                        <TableCell>
                                                                            <Chip
                                                                                label={user.status === "1" ? "active" : "inactive"}
                                                                                color={user.status === "1" ? "success" : "error"}
                                                                                size="small"
                                                                            />
                                                                        </TableCell>
                                                                    </TableRow>
                                                                ))}
                                                            </TableBody>
                                                        </Table>
                                                    ) : (
                                                        <Typography variant="body2" sx={{ py: 2, px: 1 }}>
                                                            No users found under this company.
                                                        </Typography>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            )
            }

            {/* create modal */}
            <Modal open={openNew} onClose={() => setOpenNew(false)}>
                <div className="bg-white p-6 rounded-lg shadow-lg mx-auto w-1/3 mt-20">
                    <h2 className="text-lg font-semibold mb-4">Create New Company</h2>

                    {isCompanyBlocked && (
                        <Typography variant="body2" color="error" sx={{ mb: 2 }}>
                            Cannot create a company. No admin spaces available.
                        </Typography>
                    )}

                    <TextField
                        fullWidth
                        label="Company Name"
                        variant="outlined"
                        value={newCompanyName}
                        disabled={isCompanyBlocked}
                        onChange={(e) => setNewCompanyName(e.target.value)}
                        sx={{ mb: 2 }}
                    />

                    <TextField
                        select
                        fullWidth
                        label="Space to Duplicate"
                        name="company_id"
                        value={selectedAdminSpace}
                        disabled={isCompanyBlocked}
                        onChange={(e) => setSelectedAdminSpace(e.target.value)}
                        required
                        sx={{ mb: 2 }}
                    >
                        {adminSpaces.map((space) => (
                            <MenuItem key={space.id} value={space.id.toString()}>
                                {space.space_name}
                            </MenuItem>
                        ))}
                    </TextField>

                    <div className="flex justify-end mt-4">
                        <Button onClick={() => {
                            setOpenNew(false);
                            setNewCompanyName('');
                            setSelectedAdminSpace('');
                        }} className="mr-2">
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleAddNew}
                            disabled={!newCompanyName.trim() || !selectedAdminSpace || isCompanyBlocked}
                        >
                            Save
                        </Button>
                    </div>
                </div>
            </Modal>

            {/* edit modal */}
            <Modal open={openEdit} onClose={() => setOpenEdit(false)}>
                <div className="bg-white p-6 rounded-lg shadow-lg mx-auto w-1/3 mt-20">
                    <h2 className="text-lg font-semibold mb-4">Edit Company ({editFormData.company_name})</h2>
                    <TextField
                        fullWidth
                        label="Company Name"
                        variant="outlined"
                        value={editFormData.company_name}
                        onChange={(e) =>
                            setEditFormData({ ...editFormData, company_name: e.target.value })
                        }
                        sx={{ mb: 2 }}
                    />
                    <TextField
                        select
                        fullWidth
                        label="Status"
                        value={editFormData.status}
                        onChange={(e) =>
                            setEditFormData({ ...editFormData, status: e.target.value })
                        }
                    >
                        <MenuItem value="1">Active</MenuItem>
                        <MenuItem value="2">Inactive</MenuItem>
                    </TextField>
                    <div className="flex justify-end mt-4">
                        <Button onClick={() => {
                            setOpenEdit(false);
                            setEditFormData({ id: '', company_name: '', status: '' });
                        }} className="mr-2">
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleEdit}
                            disabled={!editFormData.company_name.trim()}
                        >
                            Save
                        </Button>
                    </div>
                </div>
            </Modal>

            {/* delete dialog */}
            <ConfirmDialog
                open={openDelete}
                onClose={() => setOpenDelete(false)}
                onConfirm={handleDelete}
                title="Delete Company"
                message="Are you sure you want to delete this company? This action cannot be undone."
            />
        </Layout >
    );
};

export default CompanyList;
